/**
 * OS RÓTULOS DE EXIBIÇÃO DOS CARREGADORES — numeração e apelido de fabricante.
 *
 * ---------------------------------------------------------------------------
 * ESTE MÓDULO NÃO TOCA EM DADO. Ele só decide o que a tela ESCREVE.
 *
 * Existe separado da página por duas razões, e as duas são de verificabilidade:
 * a numeração tem uma armadilha (a lista é filtrada) e o apelido tem um custo
 * (log e tela passam a discordar). Nenhuma das duas se prova numa expressão
 * inline no JSX — foi essa a lição de `rotuloDoStatusDoConector`.
 */

/* ===========================================================================
 * 1. NUMERAÇÃO — carregador 1/2/3, conector N.1 e N.2
 * ========================================================================= */

/**
 * A ORDEM É POR `cpId`, ASCENDENTE. E a escolha do critério é o conserto.
 *
 * ---------------------------------------------------------------------------
 * POR QUE NÃO O `id` DO BANCO
 *
 * Ordenar por `id` dá 2, 19, 20 → 1, 2, 3, que é o resultado que o usuário
 * quer hoje. Mas `id` é sequência interna do Postgres: ele muda se alguém
 * recadastrar um carregador, e aí o "carregador 2" vira outro equipamento sem
 * nada na tela mudar de aparência. Um número que o operador usa para falar
 * ("o 2 está em falha") não pode depender de detalhe de armazenamento.
 *
 * `cpId` é o identificador que o EQUIPAMENTO declara no handshake OCPP. Ele
 * está na URL, no log e no chamado de suporte, e não muda por recadastro.
 * Ordem alfabética sobre ele é determinística e reproduzível por quem olha a
 * tela: `D24030465 < D24030466 < D24030483` → 1, 2, 3.
 *
 * ---------------------------------------------------------------------------
 * O QUE ACONTECE COM UM QUARTO CARREGADOR — e isto é limitação, não detalhe.
 *
 * Ele entra na posição alfabética dele. Um `cpId` que comece por letra anterior
 * a `D` vira o **1**, e os três de hoje viram 2, 3 e 4. Renumeração silenciosa.
 *
 * Isso é inerente a numerar por posição: o número é ORDINAL, não IDENTIDADE.
 * É exatamente por isso que o `cpId` continua na tela — quem precisa apontar
 * para um equipamento específico aponta pelo `cpId`, e o número serve para
 * falar rápido sobre o que está à vista.
 *
 * A alternativa seria um número gravado no banco (coluna `label`), e ela foi
 * DESCARTADA: a sincronia da VGPL também escreve `label` nos 38 carregadores
 * dela, então gravar ali criaria duas fontes para o mesmo campo — o mecanismo
 * que produziu os piores defeitos desta fase. Derivar na exibição funciona para
 * os 3 de hoje e para os 41 de amanhã, sem disputar campo com ninguém.
 *
 * ---------------------------------------------------------------------------
 * A ARMADILHA: NUMERAR SOBRE A LISTA FILTRADA.
 *
 * A tela tem filtro de texto e de local. Se o número saísse da posição na lista
 * VISÍVEL, filtrar por "Pátio A" faria o carregador 3 virar 1 — o número mudaria
 * sem o equipamento mudar, que é a forma mais direta de um rótulo mentir.
 *
 * Por isso a numeração é calculada sobre a lista COMPLETA e consultada por
 * `cpId`. Há teste varrendo isso.
 */
export function numeracaoDeCarregadores(
  todos: ReadonlyArray<{ cpId: string }>,
): ReadonlyMap<string, number> {
  const ordenados = [...todos]
    .map((c) => c.cpId)
    /* `localeCompare` e não `<`: com `cpId` alfanumérico a comparação binária
       ordena maiúscula antes de minúscula e joga `a…` depois de `Z…`. O
       operador lê a lista como lista, não como tabela ASCII. */
    .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }));
  const mapa = new Map<string, number>();
  ordenados.forEach((cpId, i) => {
    /* `has` porque `cpId` duplicado não pode consumir duas posições: se a rota
       devolver o mesmo carregador duas vezes, o número do primeiro vale. */
    if (!mapa.has(cpId)) mapa.set(cpId, mapa.size + 1);
  });
  return mapa;
}

/**
 * O rótulo do carregador: o número, ou o `cpId` quando não há número.
 *
 * `null` acontece quando o `cpId` não está na lista que gerou a numeração —
 * lista vazia, ou um carregador que chegou depois do cálculo. Nesse caso a tela
 * mostra o `cpId`, que é sempre verdadeiro, em vez de um número inventado.
 */
export function rotuloDoCarregador(
  cpId: string,
  numeracao: ReadonlyMap<string, number>,
): string {
  const n = numeracao.get(cpId);
  return n === undefined ? cpId : String(n);
}

/**
 * O rótulo do conector: `N.M`.
 *
 * ---------------------------------------------------------------------------
 * O CONECTOR `0` NÃO TEM RÓTULO, e isso é regra de OCPP, não preferência.
 *
 * Na OCPP 1.6, `connectorId = 0` designa **o ponto de recarga em si** — é por
 * onde passam `Reset`, `Heartbeat` e `StatusNotification` da estação. Não é um
 * conector físico, e "1.0" seria um bocal que não existe no pátio.
 *
 * A tela já filtra `connectorId > 0` antes de renderizar, e esta função devolve
 * `null` para o zero como segunda barreira: se alguém remover o filtro, o
 * rótulo não nasce em vez de nascer errado.
 */
export function rotuloDoConector(
  cpId: string,
  connectorId: number,
  numeracao: ReadonlyMap<string, number>,
): string | null {
  if (!Number.isInteger(connectorId) || connectorId <= 0) return null;
  const n = numeracao.get(cpId);
  /* Sem número de carregador não há `N.M` a formar. O chamador cai para o
     `#connectorId` de antes, que é verdadeiro ainda que menos legível. */
  return n === undefined ? null : `${n}.${connectorId}`;
}

/* ===========================================================================
 * 2. APELIDO DE FABRICANTE — telluspower → Nansen
 * ========================================================================= */

/**
 * APELIDO DE **EXIBIÇÃO**. O DADO NÃO MUDA, e isso é decisão explícita.
 *
 * ---------------------------------------------------------------------------
 * POR QUE NÃO SE CORRIGE O BANCO
 *
 * `vendor` é gravado a partir do `BootNotification` e **regravado a cada
 * reconexão** (`apps/server/src/ocpp.ts`, no tratamento do boot):
 * `chargePointVendor` é obrigatório no schema OCPP, então o `??` nunca preserva
 * valor antigo. Uma correção manual no banco duraria até o próximo boot do
 * equipamento — que, num CP que reconecta sozinho, são minutos.
 *
 * É o mesmo padrão do `msg_count` que reescrevia a correção manual a cada 30 s
 * nesta mesma fase: o dado tem um ESCRITOR VIVO, e disputar campo com escritor
 * vivo é perder.
 *
 * Então o banco continua dizendo `telluspower`, que é a verdade do equipamento,
 * e a tela mostra `Nansen`, que é como o usuário chama o fornecedor.
 *
 * ---------------------------------------------------------------------------
 * O CUSTO, ESCRITO PARA QUEM FOR INVESTIGAR UM CHAMADO
 *
 * **Log e suporte dizem `telluspower`; a tela diz `Nansen`.** Quem abrir o log
 * do servidor, o `BootNotification` ou o banco vai ler `telluspower`, e o print
 * que o usuário anexou ao chamado vai dizer `Nansen`. São o mesmo equipamento.
 *
 * ONDE O APELIDO É APLICADO: só em `apelidoDeFabricante`, e só no que a tela
 * escreve. Nenhuma consulta, filtro, ordenação ou chamada de API passa por
 * aqui — `vendor` cru continua sendo o valor de comparação em todo o resto.
 *
 * COMO DESFAZER: esvaziar `APELIDOS_DE_FABRICANTE`. A tela volta a mostrar o
 * valor do banco no mesmo render, sem migração e sem tocar em dado.
 */
export const APELIDOS_DE_FABRICANTE: ReadonlyMap<string, string> = new Map([
  /*
    O ÚNICO apelido, e ele é o que o usuário pediu.

    Os 38 carregadores da VGPL já trazem `Nansen` de verdade no cadastro e não
    passam por apelido nenhum — normalizar outros fabricantes por conta própria
    seria inventar política que ninguém pediu, e cada entrada aqui é uma linha a
    mais de divergência entre log e tela.
  */
  ['telluspower', 'Nansen'],
]);

/**
 * O fabricante como a TELA escreve.
 *
 * A comparação é por minúsculas e sem espaço em volta porque `vendor` vem do
 * equipamento: `"TellusPower"`, `"telluspower "` e `"TELLUSPOWER"` são o mesmo
 * fornecedor, e um apelido que só casa uma grafia deixa as outras passando —
 * seria a mesma classe do `status: "   "` que atravessou o `||` nesta sessão.
 *
 * O que NÃO está no mapa atravessa intacto, inclusive o `null`.
 */
export function apelidoDeFabricante(vendor: string | null | undefined): string | null {
  if (vendor === null || vendor === undefined) return null;
  const chave = vendor.trim().toLowerCase();
  if (chave.length === 0) return null;
  return APELIDOS_DE_FABRICANTE.get(chave) ?? vendor.trim();
}

/**
 * O fabricante TEM apelido? A tela usa para decidir se mostra a procedência.
 *
 * Um apelido que ninguém consegue relacionar ao log é uma armadilha; a tela
 * mostra o valor real ao lado (em `title`), e é esta função que diz quando.
 */
export function temApelido(vendor: string | null | undefined): boolean {
  if (vendor === null || vendor === undefined) return false;
  return APELIDOS_DE_FABRICANTE.has(vendor.trim().toLowerCase());
}
